import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Ho4ArticleSidebar from '../components/Ho4ArticleSidebar';
import { HO4_LAST_MODIFIED } from '../data/ho4Articles';

function Ho4VsHomeownersArticlePage() {
  const navigate = useNavigate();
  const articleLinkStyle = { color: '#065F46', fontWeight: 600, textDecoration: 'underline' };

  useEffect(() => {
    const previousTitle = document.title;
    const descriptionMeta = document.querySelector('meta[name="description"]');
    const previousDescription = descriptionMeta?.getAttribute('content') ?? '';

    document.title = 'HO-4 vs Homeowners Insurance: What Is Different? | GetSkimp';

    if (descriptionMeta) {
      descriptionMeta.setAttribute(
        'content',
        'Learn the key differences between HO-4 renters insurance and homeowners insurance, including what each policy covers and who needs it.'
      );
    }

    return () => {
      document.title = previousTitle;
      if (descriptionMeta) {
        descriptionMeta.setAttribute('content', previousDescription);
      }
    };
  }, []);

  return (
    <div className="container">
      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: '2rem' }}>
        <Ho4ArticleSidebar
          activeHref="/knowledge-base/how-is-ho4-renters-insurance-different-from-homeowners-insurance"
          onNavigate={(href) => navigate(href)}
        />

        <section>
          <div style={{ marginBottom: '1rem' }}>
            <button
              type="button"
              className="btn btn-sm btn-outline"
              onClick={() => navigate('/knowledge-base')}
              style={{ marginBottom: '1rem' }}
            >
              Back to self help library
            </button>
            <h2 style={{ marginBottom: '0.5rem' }}>
              How is HO-4 different from other homeowners insurance?
            </h2>
            <p style={{ marginBottom: 0, color: '#4B5563' }}>
              A simple comparison of what renters insurance covers versus homeowners policy forms.
            </p>
            <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#6B7280' }}>
              Last modified: {HO4_LAST_MODIFIED}
            </p>
          </div>

          <article className="card mb-4">
            <div className="card-body">
              <h3 style={{ marginBottom: '0.75rem' }}>Quick answer</h3>
              <p>
                <Link to="/knowledge-base/what-is-ho4-renters-insurance" style={articleLinkStyle}>
                  HO-4
                </Link>{' '}
                is for renters and mainly covers your belongings, liability, and loss of use. Homeowners policies are
                for people who own a home and include coverage for the building itself. If you rent, HO-4 is generally
                the policy type you need.
              </p>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>Core difference in one line</h3>
              <p>
                <strong>Renters insurance (HO-4)</strong> protects your stuff and liability in a unit you do not own.
                <strong> Homeowners insurance</strong> protects both the home structure and your personal property.
              </p>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>Coverage comparison</h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>
                        Coverage area
                      </th>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>
                        HO-4 (renters)
                      </th>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>
                        Homeowners policy
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Dwelling/building</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>No</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Yes</td>
                    </tr>
                    <tr>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Personal property</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Yes</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Yes</td>
                    </tr>
                    <tr>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Personal liability</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Yes</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Yes</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '0.6rem' }}>Loss of use</td>
                      <td style={{ padding: '0.6rem' }}>Yes</td>
                      <td style={{ padding: '0.6rem' }}>Yes</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>Who should buy HO-4?</h3>
              <ul style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>Apartment renters and condo renters.</li>
                <li style={{ marginBottom: '0.5rem' }}>Students in off-campus housing.</li>
                <li style={{ marginBottom: '0.5rem' }}>Anyone whose landlord requires proof of renters insurance.</li>
                <li>
                  Renters who want liability protection beyond the lease minimum. Compare options with this article on{' '}
                  <Link to="/knowledge-base/how-much-liability-do-i-need-for-renters-insurance" style={articleLinkStyle}>
                    how much liability you may need
                  </Link>
                  .
                </li>
              </ul>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>Common confusion to avoid</h3>
              <ul style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>
                  Your landlord&apos;s policy usually does not cover your personal belongings.
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  Homeowners policy forms are not interchangeable with renters insurance.
                </li>
                <li>Always check endorsements and exclusions for your exact policy.</li>
              </ul>
              <p style={{ marginTop: '1rem', marginBottom: 0 }}>
                Ready to purchase? Follow this step-by-step guide on{' '}
                <Link to="/knowledge-base/how-to-get-ho4-renters-insurance" style={articleLinkStyle}>
                  how to get HO-4 renters insurance
                </Link>
                .
              </p>
            </div>
          </article>

          <section
            className="card mb-4"
            style={{
              borderColor: 'var(--color-green)',
              background: 'linear-gradient(135deg, #ECFDF5 0%, #FFFFFF 100%)',
              boxShadow: 'var(--shadow-md)',
            }}
          >
            <div className="card-body">
              <h3 style={{ marginBottom: '0.5rem', color: '#065F46' }}>Start renters insurance with GetSkimp</h3>
              <p style={{ marginBottom: '1rem' }}>
                If you rent your home, we can help you choose HO-4 coverage that matches your needs.
              </p>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => navigate('/qualify')}
                style={{ fontWeight: 700, paddingInline: '1.4rem' }}
              >
                Get started now
              </button>
            </div>
          </section>

          <section
            className="card"
            style={{
              borderColor: '#FBBF24',
              background: 'linear-gradient(135deg, #FFFBEB 0%, #FFFFFF 100%)',
            }}
          >
            <div className="card-body">
              <h3 style={{ marginBottom: '0.5rem', color: '#92400E' }}>Want help picking the right policy type?</h3>
              <p style={{ marginBottom: '1rem' }}>
                Speak with an agent if you want help confirming HO-4 is the right fit for your rental situation.
              </p>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => navigate('/help')}
                style={{ borderColor: '#D97706', color: '#92400E', fontWeight: 700 }}
              >
                Speak to an agent
              </button>
            </div>
          </section>
        </section>
      </div>
    </div>
  );
}

export default Ho4VsHomeownersArticlePage;
